import React, { useState, useEffect } from "react";
import { Trash2, Star, MessageSquare, MapPin, User, Search, Edit } from "lucide-react";
import { toast } from "react-toastify";
import API_URL from "../../config";
import ConfirmModal from "./ConfirmModal";
import EditReviewModal from "../Reviews/EditReviewModal";

export default function ManageReviews() {
    const [reviews, setReviews] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);

    const fetchReviews = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`${API_URL}/reviews`);
            const data = await response.json();
            if (response.ok) {
                setReviews(data.reviews || []);
            }
        } catch (error) {
            console.error("Error fetching reviews:", error);
            toast.error("Failed to load reviews");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
    }, []);

    const handleDeleteClick = (review) => {
        setSelectedReview(review);
        setIsConfirmModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!selectedReview) return;

        try {
            const response = await fetch(`${API_URL}/reviews/${selectedReview._id}`, {
                method: "DELETE",
            });
            if (response.ok) {
                toast.success("Review deleted successfully");
                fetchReviews();
            } else {
                toast.error("Failed to delete review");
            }
        } catch (error) {
            console.error("Error deleting review:", error);
            toast.error("Error deleting review");
        } finally {
            setIsConfirmModalOpen(false);
            setSelectedReview(null);
        }
    };

    const handleEditClick = (review) => {
        setSelectedReview(review);
        setIsEditModalOpen(true);
    };

    const filteredReviews = reviews.filter(
        (review) =>
            review.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            review.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
            review.comment.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Manage Customer Reviews</h2>
                    <p className="text-sm text-gray-500">Monitor and manage all user-submitted reviews</p>
                </div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search reviews..."
                        className="pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none w-full md:w-64 transition"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-12">
                    <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : filteredReviews.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                    {filteredReviews.map((review) => (
                        <div key={review._id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition group">
                            <div className="flex flex-col md:flex-row justify-between gap-4">
                                <div className="space-y-3 flex-1">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 font-bold">
                                            {review.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 flex items-center gap-2">
                                                <User size={14} className="text-gray-400" />
                                                {review.name}
                                            </h4>
                                            <p className="text-xs text-gray-500 flex items-center gap-1">
                                                <MapPin size={12} />
                                                {review.destination}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-1">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <Star
                                                key={star}
                                                size={16}
                                                className={star <= review.rating ? "fill-amber-400 text-amber-400" : "text-gray-200"}
                                            />
                                        ))}
                                        <span className="text-xs font-bold text-gray-400 ml-2">{new Date(review.createdAt || Date.now()).toLocaleDateString()}</span>
                                    </div>

                                    <div className="bg-gray-50 p-3 rounded-xl">
                                        <p className="text-sm text-gray-700 italic flex gap-2">
                                            <MessageSquare size={16} className="text-gray-300 shrink-0" />
                                            "{review.comment}"
                                        </p>
                                    </div>
                                </div>

                                <div className="flex md:flex-col justify-end gap-2">
                                    <button
                                        onClick={() => handleEditClick(review)}
                                        className="p-2.5 bg-sky-50 text-sky-600 rounded-xl hover:bg-sky-100 transition-colors flex items-center gap-2 text-sm font-bold"
                                        title="Edit Review"
                                    >
                                        <Edit size={18} />
                                        <span className="md:hidden">Edit</span>
                                    </button>
                                    <button
                                        onClick={() => handleDeleteClick(review)}
                                        className="p-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors flex items-center gap-2 text-sm font-bold"
                                        title="Delete Review"
                                    >
                                        <Trash2 size={18} />
                                        <span className="md:hidden">Delete</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                    <MessageSquare size={48} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium">No reviews found matching your search</p>
                </div>
            )}

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Review"
                message="Are you sure you want to delete this review? This action cannot be undone."
                confirmText="Delete"
            />

            <EditReviewModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                review={selectedReview}
                onUpdate={fetchReviews}
            />
        </div>
    );
}
